import useSWR from 'swr';
import useSWRImmutable from 'swr/immutable';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { NavigationContext } from '../../Navigation/NavigationProvider';
import { useOrganization } from '../../Settings/SettingsDropDownMenu';
import { fetchService } from '../../../utils/fetchService';
import { GuardianReportHeaderAndMetadata } from './GuardianReportHeaderAndMetadata';
import PolicyViolations from './PolicyViolations/PolicyViolations';
import { ActionsButtonHeader } from './ActionsButtonHeader';
import { RootTabs } from './tabs/RootTabs';
import { transformRuleSummaries } from './utils/dopplerUtil';
import { useFetchRuleSummaries } from './utils/useFetchRuleSummaries';
import { PdfViewOptionsModal } from './PdfReport/PdfViewOptionsModal';
import { QuokkaEvent, useSubscribeToEvent } from '../../../utils/pubSub';
import { PubSubEvents as Events } from '@kw/constants';
import { OrgPolicyResults } from './types/ApplicationDetailsType';
import { isBehavioralRuleSummary, isPermissionOverviewRuleSummary, isRuleOverviewRuleSummary } from './utils/helper';
import { appIconDataUrl } from '../../../utils/appIconDataUrl';
import { getReportDisplayVersion } from '../appVersionDisplay';

const fetcher = accessToken => path => fetchService(path, accessToken);

export function SarifReportContent({ analysisId, accessToken, backQParams }) {
  const {
    data: { appDetailsResponse, aggregateData, sbomData, threatScoreData },
    mutate,
  } = useSWR(
    [
      `/eval/applications/appDetails/${analysisId}`,
      `/eval/results/${analysisId}/aggregate-data`,
      `/eval/results/${analysisId}/sbom`,
      `/eval/results/${analysisId}/threat-score`,
    ],
    fetchDataInParallel(accessToken),
    {
      suspense: true,
      revalidateIfStale: true,
      revalidateOnMount: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  const [latestAppDetailsResponse, setLatestAppDetailsResponse] = useState(null);

  const { data: latestAppDetailsResponseData } = useSWR(`/eval/applications/latestAnalysis/${analysisId}`, fetcher(accessToken), {
    revalidateOnFocus: false,
  });

  useEffect(() => {
    if (latestAppDetailsResponseData) {
      setLatestAppDetailsResponse(latestAppDetailsResponseData);
    }
  }, [latestAppDetailsResponseData]);

  const { ruleSummariesData } = useFetchRuleSummaries(analysisId, {
    suspense: true,
    revalidateIfStale: true,
    revalidateOnMount: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const { appDetails: latestAppDetails, appAnalysis: latestAppAnalysis } = latestAppDetailsResponse ?? appDetailsResponse;

  // Fetch App Details
  const { appName, appId, version, os } = latestAppDetails;
  // `version` stays the analysis subject's identity (used for the analysis-update event match below);
  // only the rendered label is cleaned up for uploaded binaries.
  const displayVersion = getReportDisplayVersion(latestAppDetails);
  const iconUrl = appIconDataUrl(latestAppDetails.appIconBase64);
  const triggeredPolicies = useMemo<OrgPolicyResults[]>(
    () => latestAppAnalysis?.orgPolicyResults?.filter((p: OrgPolicyResults) => p.didTrigger) ?? [],
    [latestAppAnalysis?.orgPolicyResults],
  );
  const hasPolicyViolation = triggeredPolicies.length > 0;

  useSubscribeToEvent(Events.APP_ANALYSIS_COMPLETED);

  useEffect(() => {
    const handler = async (eventData: { name?: string; message?: string; description?: string; sender?: string }) => {
      const { appId: eventAppId, os: eventOs, version: eventVersion } = JSON.parse(eventData.message);
      if (appId === eventAppId && os === eventOs && version === eventVersion) {
        try {
          const data = await fetchService(`/eval/applications/latestAnalysis/${analysisId}`, accessToken);
          if (data) {
            setLatestAppDetailsResponse(data);
          }
        } catch {
          // ignore transient fetch errors for socket-driven refresh
        }
      }
    };
    QuokkaEvent.on(Events.APP_ANALYSIS_COMPLETED, handler);
  }, []);

  const { fullRuleSummaries, nonBehavioralCategoriesCount, behavioralCategoriesCount } = useMemo(
    () => transformRuleSummaries(ruleSummariesData),
    [ruleSummariesData],
  );

  const malwareRule = fullRuleSummaries?.find(ruleSummary => ruleSummary.rule?.id?.startsWith('malware/'));

  const { data: maliciousData } = useSWRImmutable(
    malwareRule?.guid ? `/eval/results/${analysisId}/evidence?ruleGuid=${malwareRule?.guid}` : null,
    fetcher(accessToken),
  );
  const maliciousResult = maliciousData?.entries && maliciousData.entries.length > 0 ? maliciousData.entries[0].result : {};

  const { setAppName } = useContext(NavigationContext);

  const { orgData } = useOrganization(accessToken);
  const showDeniedApps = orgData?.settings?.includes('denied-apps');

  const isDeniedApp = showDeniedApps && latestAppAnalysis.isDenied;
  const isTrustedApp = latestAppAnalysis.isAllowed;

  const { state } = useLocation();
  const pdfOptionsOpen = Boolean(state?.pdfOptionsOpen);

  const [isPdfOptionsOpen, setIsPdfOptionsOpen] = useState(false);

  const nonBehavioralRuleCategoryOptions = [
    { value: 'all', label: 'All' },
    ...Object.entries(nonBehavioralCategoriesCount).map(([category, count]) => ({
      label: `${category} (${count})`,
      value: category,
    })),
  ];
  const behavioralRuleCategoryOptions = [
    { value: 'all', label: 'All' },
    ...Object.entries(behavioralCategoriesCount).map(([category, count]) => ({
      label: `${category} (${count})`,
      value: category,
    })),
  ];

  useEffect(() => {
    setAppName(appName);
    setIsPdfOptionsOpen(pdfOptionsOpen);
  }, [appName, pdfOptionsOpen]);

  let tag: { label: string; color: string; iconSrc: string } | undefined;

  if (isDeniedApp) {
    tag = { label: 'Denied App', color: '#FF0000', iconSrc: '/icons/shield-x-red.svg' };
  } else if (isTrustedApp) {
    tag = { label: 'Trusted App', color: '#00B300', iconSrc: '/icons/shield-checked-green.svg' };
  } else if (hasPolicyViolation) {
    tag = { label: 'Policy Violation', color: '#FF0000', iconSrc: '/icons/alert-triangle-red.svg' };
  }

  const collectRuleIds = useCallback(
    (predicate: (rs: any) => boolean) =>
      new Set<string>(
        (fullRuleSummaries ?? [])
          .filter(predicate)
          .map(rs => rs.rule?.id)
          .filter(Boolean),
      ),
    [fullRuleSummaries],
  );

  const customRuleIds = ['HIGHRISK_COUNTRY_CONNECTION', 'POSSIBLE_FOREIGN_CONNECTION', 'STATIC_HIGHRISK_COUNTRY_CONNECTION'];

  const ruleOverviewRuleIds = useMemo(
    () => new Set([...Array.from(collectRuleIds(isRuleOverviewRuleSummary)), ...customRuleIds]),
    [collectRuleIds],
  );
  const permissionOverviewRuleIds = useMemo(() => collectRuleIds(isPermissionOverviewRuleSummary), [collectRuleIds]);
  const behavioralRuleIds = useMemo(() => collectRuleIds(isBehavioralRuleSummary), [collectRuleIds]);

  const matchingPolicies = useCallback(
    (allowedRuleIds: Set<string>) =>
      triggeredPolicies.filter(policy =>
        policy.associatedRules?.some(rule =>
          Object.values(rule.appIorResultsMap ?? {})
            .flat()
            .some(ruleId => ruleId && allowedRuleIds.has(ruleId)),
        ),
      ),
    [triggeredPolicies],
  );

  const policyViolationsRuleOverview = useMemo(() => matchingPolicies(ruleOverviewRuleIds), [matchingPolicies, ruleOverviewRuleIds]);

  const policyViolationsPermissionOverview = useMemo(
    () => matchingPolicies(permissionOverviewRuleIds),
    [matchingPolicies, permissionOverviewRuleIds],
  );

  const policyViolationsBehavioral = useMemo(() => matchingPolicies(behavioralRuleIds), [matchingPolicies, behavioralRuleIds]);

  return (
    <>
      <PdfViewOptionsModal
        isOpen={isPdfOptionsOpen}
        toggle={() => setIsPdfOptionsOpen(!isPdfOptionsOpen)}
        appName={appName}
        analysisId={analysisId}
        overviewRuleCategoryOptions={nonBehavioralRuleCategoryOptions}
        behavioralRuleCategoryOptions={behavioralRuleCategoryOptions}
        os={os}
      />
      <ActionsButtonHeader
        appAnalysis={latestAppAnalysis}
        appDetails={latestAppDetails}
        fetchDeniedApps={mutate}
        fetchTrustedApps={mutate}
        showDeniedApps={showDeniedApps}
        backQParams={backQParams}
        setPdfOptionsOpen={setIsPdfOptionsOpen}
        analysisId={analysisId}
      />
      <GuardianReportHeaderAndMetadata
        appName={appName}
        appId={appId}
        appVersion={displayVersion}
        os={os}
        iconUrl={iconUrl}
        appDetails={latestAppDetails}
        appAnalysis={latestAppAnalysis}
        tag={tag}
        threatScore={threatScoreData?.threatScore}
      />
      <PolicyViolations
        orgPolicyResults={latestAppAnalysis?.orgPolicyResults}
        ruleOverviewRuleIds={ruleOverviewRuleIds}
        permissionOverviewRuleIds={permissionOverviewRuleIds}
        behavioralRuleIds={behavioralRuleIds}
      />

      <RootTabs
        aggregateData={aggregateData}
        ruleSummaries={fullRuleSummaries}
        analysisId={analysisId}
        maliciousResult={maliciousResult}
        os={os}
        appDetails={latestAppDetails}
        sbomData={sbomData}
        accessToken={accessToken}
        policyViolations={{ policyViolationsRuleOverview, policyViolationsPermissionOverview, policyViolationsBehavioral }}
      />
    </>
  );
}

function fetchDataInParallel(accessToken: string) {
  return async (url0, url1, url2, url3) => {
    const [appDetailsResponse, aggregateData, sbomData, threatScoreData] = await Promise.all([
      fetchService(url0, accessToken),
      fetchService(url1, accessToken),
      fetchService(url2, accessToken),
      fetchService(url3, accessToken),
    ]);
    return {
      appDetailsResponse,
      aggregateData,
      sbomData,
      threatScoreData,
    };
  };
}
